import { useEffect, useRef, useState } from "react";

interface Company {
  id: string;
  name: string;
  role: string;
  roleNote?: string;
  location: string;
  dateRange: string;
  featured: boolean;
  bullets: string[];
}

const companies: Company[] = [
  {
    id: "hp-inc",
    name: "HP Inc.",
    role: "Cloud Architect",
    roleNote: "Cloud Engineer IV (Workday)",
    location: "Boise, ID",
    dateRange: "2023–Present",
    featured: true,
    bullets: [
      "Serving as one of a small group of Cloud Architects — collaborating with fellow architects to evaluate how new solutions fit within the broader platform architecture, then working with contract teams to deliver on those designs.",
      "Pioneering a data-driven prioritization culture by introducing analytics and real customer usage metrics to a team where this was a new practice — shifting from gut-feel decisions to evidence-based roadmap decisions.",
      "Led the transformation from spreadsheet-based planning to Jira — streamlining team workflows, improving visibility, and freeing up senior capacity to focus on higher-leverage architectural work.",
      "Pushed greater ownership and accountability to contractor teams, increasing productivity across the board and creating space to invest more deeply in data and architecture.",
      "Delivering new features focused on improving customers' first experience — expanding options and driving measurable improvements in early customer journeys.",
      "Led organization-wide migration from Splunk to DataDog using an APM-first approach, including training and enablement across the team.",
    ],
  },
  {
    id: "intuit",
    name: "Intuit / TSheets",
    role: "Senior PM & Senior Software Engineer",
    location: "Boise, ID",
    dateRange: "2019–2023",
    featured: true,
    bullets: [
      "Recovered over $100K in MRR by conducting in-depth customer interviews that reshaped the billing migration experience — turning churn risk into retained revenue.",
      "Led product work that expanded TSheets from a single paid SKU to two — creating an entirely new tier through GPS and workforce features built with IDEO Design Thinking methodology.",
      "Delivered features that drove a 90% retention rate among customers who experienced them; partnered closely with the onboarding team to close the activation gap and scale feature adoption.",
      "Navigated a genuine three-sided product challenge with GPS: business owners needed clock-in enforcement, while employees needed to feel respected and not surveilled. Led design, engineering, and data through IDEO Design Thinking — iterating through customer and worker interviews — to land on a solution satisfying both sides. Result: double-digit reduction in time theft with zero increase in worker dissatisfaction.",
      "Co-led Agile training across the Dev Business Unit, increasing sprint predictability by 10%, and served as Scrum Master coaching both PMs and engineers.",
      "Led engineering initiative to define and implement the Time data entity architecture across the entire company.",
    ],
  },
  {
    id: "yeswayjoue",
    name: "YesWayJosue LLC",
    role: "CEO / Founder",
    location: "Boise, ID",
    dateRange: "2023–Present",
    featured: false,
    bullets: [
      "Founded and operating a consulting business helping clients grow their online presence through targeted SEO improvements and website redesigns.",
      "Streamlined client marketing workflows including CRM setup, outreach campaigns, email newsletters, and automation.",
      "Transitioning internal tooling into a scalable platform product to increase operational efficiency.",
    ],
  },
  {
    id: "alertsense",
    name: "AlertSense / Konexus",
    role: "Senior Software Engineer",
    location: "Boise, ID",
    dateRange: "2017–2019",
    featured: false,
    bullets: [
      "Contributed to the development of emergency notification and mass communication platforms used by government and enterprise clients.",
      "Worked across full-stack engineering to ship features under tight compliance and reliability requirements.",
      "Collaborated closely with product and design to translate complex user needs into scalable technical solutions.",
    ],
  },
  {
    id: "hewlett-packard",
    name: "Hewlett Packard",
    role: "Software Engineer",
    location: "Boise, ID",
    dateRange: "2010–2017",
    featured: false,
    bullets: [
      "Spent seven years building firmware and platform software for HP LaserJet devices — contributing to multiple product generations.",
      "Led a cross-functional team to ship a unified login platform for HP LaserJet, launching Private Print and Scan to Cloud as anchor applications.",
      "Architected the platform to support external third-party app onboarding, establishing the foundation for an ecosystem of device applications.",
    ],
  },
];

function TimelineEntry({ company, index }: { company: Company; index: number }) {
  const [expanded, setExpanded] = useState(index < 2);
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateX(-20px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = `opacity 0.7s ease-out ${index * 0.1}s, transform 0.7s ease-out ${index * 0.1}s`;
          el.style.opacity = "1";
          el.style.transform = "translateX(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div ref={ref} className="flex gap-6 lg:gap-10">
      {/* Timeline spine */}
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: "20px" }}>
        {/* Dot */}
        <div
          className="flex-shrink-0 rounded-full mt-1"
          style={{
            width: company.featured ? "12px" : "8px",
            height: company.featured ? "12px" : "8px",
            backgroundColor: company.featured ? "#C17D3C" : "rgba(255,255,255,0.2)",
            border: company.featured ? "2px solid rgba(193,125,60,0.3)" : "none",
            marginLeft: company.featured ? "-2px" : "0",
          }}
        />
        {/* Line */}
        {index < companies.length - 1 && (
          <div
            className="flex-1 mt-3"
            style={{ width: "1px", backgroundColor: "rgba(255,255,255,0.07)", minHeight: "40px" }}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-12">
        {/* Header row */}
        <button
          className="w-full text-left cursor-pointer group"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3
                className="font-lora font-medium text-[18px] leading-snug transition-colors duration-200"
                style={{ color: expanded ? "#FAF8F4" : "rgba(250,248,244,0.6)" }}
              >
                {company.name}
              </h3>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="font-dm text-[12px]" style={{ color: "rgba(250,248,244,0.3)" }}>
                {company.dateRange}
              </span>
              <i
                className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm transition-transform duration-300`}
                style={{ color: "rgba(250,248,244,0.25)" }}
              />
            </div>
          </div>
          <p className="font-dm text-[13px]" style={{ color: "#C17D3C" }}>{company.role}</p>
          {company.roleNote && (
            <p className="font-dm text-[12px] italic mt-0.5" style={{ color: "rgba(250,248,244,0.3)" }}>{company.roleNote}</p>
          )}
        </button>

        {/* Expandable bullets */}
        <div
          ref={contentRef}
          className="overflow-hidden"
          style={{
            maxHeight: expanded ? "1000px" : "0",
            opacity: expanded ? 1 : 0,
            transition: "max-height 0.4s ease, opacity 0.3s ease",
          }}
        >
          <ul className="mt-5 space-y-3">
            {company.bullets.map((bullet, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="mt-[8px] flex-shrink-0 rounded-full"
                  style={{ width: "4px", height: "4px", backgroundColor: "#C17D3C", opacity: 0.6 }}
                />
                <span className="font-dm text-[13px] leading-[1.7]" style={{ color: "rgba(250,248,244,0.5)" }}>
                  {bullet}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function WorkExperience() {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="experience" className="py-28 lg:py-36" style={{ backgroundColor: "#140F0A" }}>
      <div className="mx-auto px-8 lg:px-16" style={{ maxWidth: "1200px" }}>

        {/* Header */}
        <div ref={headerRef} className="mb-16 lg:mb-20">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "#C17D3C" }}>02</span>
            <div style={{ width: "40px", height: "1px", backgroundColor: "#C17D3C", opacity: 0.5 }} />
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "rgba(250,248,244,0.35)" }}>Work experience</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-lora font-semibold leading-[1.15] tracking-tight"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "#FAF8F4" }}
            >
              15 years. Four companies.<br />One consistent thread.
            </h2>
            <p
              className="font-dm text-[14px] leading-[1.7]"
              style={{ color: "rgba(250,248,244,0.4)", maxWidth: "340px" }}
            >
              Deep expertise built across product management, software engineering, and team leadership — at scale.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex flex-col">
          {companies.map((company, i) => (
            <TimelineEntry key={company.id} company={company} index={i} />
          ))}
        </div>

      </div>
    </section>
  );
}
